import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Alert, Card, Statistic } from 'antd';
import {
  SecurityScanOutlined,
  DesktopOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  FireOutlined,
  CloudServerOutlined,
  LaptopOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { useTenant } from '../context/TenantContext';
import {
  useSophosEndpoints,
  useSophosHealth,
  useSophosFirewalls,
  useSophosMdrStatus,
  useSophosIdFromCipp,
} from '../hooks';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const InfoCard = styled(Card)`
  text-align: center;
`;

const SecurityPage = () => {
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Sophos tenant ID using customer mapping service
  const {
    sophosId,
    isResolved: hasSophosMapping,
    isLoading: mappingLoading,
    customer,
  } = useSophosIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  // Fetch data using the resolved Sophos tenant ID
  const { data: endpointsData, isLoading: endpointsLoading } = useSophosEndpoints(sophosId, {
    enabled: !!sophosId,
  });
  const { data: healthData, isLoading: healthLoading } = useSophosHealth(sophosId, {
    enabled: !!sophosId,
  });
  const { data: firewallsData, isLoading: firewallsLoading } = useSophosFirewalls(sophosId, {
    enabled: !!sophosId,
  });
  const { data: mdrData, isLoading: mdrLoading } = useSophosMdrStatus(sophosId, {
    enabled: !!sophosId,
  });

  // Parse endpoint summary (get_customer_endpoint_inventory response format)
  // Response: { success, data: { customer, summary: { total, workstations: {count, healthy, suspicious, bad}, servers: {...} } } }
  const summary = useMemo(() => {
    const s = endpointsData?.data?.summary || endpointsData?.summary || {};
    const workstations = s.workstations || {};
    const servers = s.servers || {};
    return {
      total: s.total || (workstations.count || 0) + (servers.count || 0),
      workstations,
      servers,
    };
  }, [endpointsData]);

  // Parse health data (get_customer_endpoint_health response format)
  // Response: { success, data: { healthStatus, threats: { atRisk, compromised }, ... } }
  const health = useMemo(() => {
    return healthData?.data || healthData || {};
  }, [healthData]);

  // Calculate aggregate stats
  const endpointCount = summary.total;
  const healthyEndpoints = (summary.workstations.healthy || 0) + (summary.servers.healthy || 0);
  const warningEndpoints = (summary.workstations.suspicious || 0) + (summary.servers.suspicious || 0);
  const criticalEndpoints = (summary.workstations.bad || 0) + (summary.servers.bad || 0);

  // Parse threats
  const threats = health.threats || {};
  const atRiskCount = threats.atRisk || 0;
  const compromisedCount = threats.compromised || 0;

  // Parse firewalls
  const firewalls = useMemo(() => {
    const parsed = firewallsData?.data?.firewalls || firewallsData?.firewalls || [];
    return Array.isArray(parsed) ? parsed : [];
  }, [firewallsData]);

  // Parse MDR status
  const mdr = useMemo(() => {
    const d = mdrData?.data;
    if (!d) return null;
    return { enabled: d.hasMdr === true, tier: d.tier || null, active: d.active };
  }, [mdrData]);

  // Show mapping warning if CIPP tenant selected but no Sophos mapping found
  const showMappingWarning = cippTenantId && !hasSophosMapping && !mappingLoading;

  // Combined loading state
  const isLoading = mappingLoading || endpointsLoading || healthLoading;

  // Endpoint type breakdown table rows
  const breakdownRows = useMemo(() => {
    const rows = [];
    const ws = summary.workstations;
    const sv = summary.servers;
    if (ws.count > 0 || endpointsData) {
      rows.push({
        key: 'workstations',
        type: 'Workstations',
        icon: <LaptopOutlined />,
        total: ws.count || 0,
        healthy: ws.healthy || 0,
        suspicious: ws.suspicious || 0,
        bad: ws.bad || 0,
      });
    }
    if (sv.count > 0 || endpointsData) {
      rows.push({
        key: 'servers',
        type: 'Servers',
        icon: <CloudServerOutlined />,
        total: sv.count || 0,
        healthy: sv.healthy || 0,
        suspicious: sv.suspicious || 0,
        bad: sv.bad || 0,
      });
    }
    return rows;
  }, [summary, endpointsData]);

  const breakdownColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {record.icon} {type}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
    },
    {
      title: 'Healthy',
      dataIndex: 'healthy',
      key: 'healthy',
      render: (v) => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Warnings',
      dataIndex: 'suspicious',
      key: 'suspicious',
      render: (v) => <Tag color={v > 0 ? 'orange' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Critical',
      dataIndex: 'bad',
      key: 'bad',
      render: (v) => <Tag color={v > 0 ? 'red' : 'default'}>{v}</Tag>,
    },
  ];

  const firewallColumns = [
    {
      title: 'Hostname',
      dataIndex: 'hostname',
      key: 'hostname',
      render: (v) => v || 'Unknown',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (v) => v || '--',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const isOnline = typeof record.status === 'object'
          ? record.status?.connected === true && !record.status?.suspended
          : record.status === 'online' || record.connectionStatus === 'connected';
        return <Tag color={isOnline ? 'green' : 'red'}>{isOnline ? 'Online' : 'Offline'}</Tag>;
      },
    },
    {
      title: 'HA Role',
      key: 'ha',
      render: (_, record) => {
        const role = record.cluster?.status;
        if (!role || role === 'standalone') return <Tag>Standalone</Tag>;
        return <Tag color={role === 'primary' ? 'green' : 'blue'}>{role}</Tag>;
      },
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <SecurityScanOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Sophos
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant
            ? `${selectedTenant.displayName}${customer ? ` (${customer.displayName})` : ''} - Endpoint Protection`
            : 'All Tenants - Sophos Central'}
        </Text>
      </PageHeader>

      {/* Mapping Warning */}
      {showMappingWarning && (
        <Alert
          message="No Sophos Mapping Found"
          description={
            <>
              The selected tenant does not have a Sophos ID configured in the customer mappings.
              Add a mapping in <code>config/customer-mappings.json</code> to view Sophos data.
            </>
          }
          type="warning"
          showIcon
          icon={<LinkOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={DesktopOutlined}
            value={endpointCount || null}
            label="Total Endpoints"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CheckCircleOutlined}
            value={endpointsData ? healthyEndpoints : null}
            label="Healthy"
            color="var(--odin-success)"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={ExclamationCircleOutlined}
            value={endpointsData ? warningEndpoints : null}
            label="Warnings"
            color="var(--odin-warning)"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CloseCircleOutlined}
            value={endpointsData ? criticalEndpoints : null}
            label="Critical"
            color="var(--odin-error)"
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* Threats + MDR row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <InfoCard>
            <Statistic
              title="Endpoints At Risk"
              value={healthData ? atRiskCount : '--'}
              prefix={<WarningOutlined style={{ color: atRiskCount > 0 ? 'var(--odin-warning)' : 'var(--odin-success)' }} />}
              loading={healthLoading}
            />
          </InfoCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoCard>
            <Statistic
              title="Compromised"
              value={healthData ? compromisedCount : '--'}
              prefix={<CloseCircleOutlined style={{ color: compromisedCount > 0 ? 'var(--odin-error)' : 'var(--odin-success)' }} />}
              loading={healthLoading}
            />
          </InfoCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoCard>
            <Statistic
              title="Overall Health"
              value={health.healthStatus ? health.healthStatus.charAt(0).toUpperCase() + health.healthStatus.slice(1) : '--'}
              prefix={<SafetyCertificateOutlined style={{
                color: health.healthStatus === 'good' ? 'var(--odin-success)'
                  : health.healthStatus === 'suspicious' ? 'var(--odin-warning)' : 'var(--odin-error)'
              }} />}
              loading={healthLoading}
            />
          </InfoCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <InfoCard>
            <Statistic
              title="MDR"
              value={mdr === null ? '--' : mdr.enabled ? (mdr.tier || 'Active') : 'Not Licensed'}
              prefix={<SafetyCertificateOutlined style={{ color: mdr?.enabled ? 'var(--odin-success)' : 'var(--odin-warning)' }} />}
              loading={mdrLoading}
            />
          </InfoCard>
        </Col>
      </Row>

      {/* Endpoint Breakdown + Firewalls */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={firewalls.length > 0 ? 14 : 24}>
          <DataCard
            title="Endpoint Breakdown"
            icon={DesktopOutlined}
            loading={isLoading}
            empty={!endpointsData && !showMappingWarning}
            emptyText={
              showMappingWarning
                ? 'Configure Sophos mapping to view endpoints'
                : selectedTenant
                ? 'No endpoint data found'
                : 'Select a tenant to view endpoints'
            }
          >
            <StyledTable
              dataSource={breakdownRows}
              columns={breakdownColumns}
              rowKey="key"
              pagination={false}
              size="small"
            />
          </DataCard>
        </Col>

        {(firewalls.length > 0 || firewallsLoading) && (
          <Col xs={24} lg={10}>
            <DataCard
              title="Firewalls"
              icon={FireOutlined}
              loading={firewallsLoading}
              empty={!firewalls.length && !firewallsLoading}
              emptyText="No firewalls found"
            >
              <StyledTable
                dataSource={firewalls}
                columns={firewallColumns}
                rowKey={(r) => r.id || r.hostname || Math.random().toString()}
                pagination={false}
                size="small"
              />
            </DataCard>
          </Col>
        )}
      </Row>
    </DashboardLayout>
  );
};

export default SecurityPage;
